import { urlId, type RecordId } from './publicIds';

// Record ids go through urlId(), which sends a loaded record's public id in
// place of its primary key (see publicIds.ts).
export const ENDPOINTS = {
  v1: {
    users: {
      findByEmail: '/v1/users/find_by_email',
      validateEmail: '/v1/users/validate_email',
      validateCoverageArea: '/v1/users/validate/coverage-area',
      siteverify: '/v1/users/siteverify',
      create: '/v1/users',
      signIn: '/v1/users/customer/sign_in',
      signOut: '/v1/users/sign_out',
      forgotPassword: '/v1/users/forgot_password',
      // Phase 6.2 (PROF-06) — Devise change-password. POSTs
      // `{ session: { current_password, password, password_confirmation } }`;
      // 200 renders `{ response: 'Password Changed' }` (NOT a customer — do NOT
      // setUser, Pitfall 5), 422 renders `{ message }`.
      // `[VERIFIED: ../api/app/controllers/v1/sessions_controller.rb:22-32;
      // routes/v1_api.rb:20]`
      changePassword: '/v1/users/customer/change_password',
      update: (id: RecordId) => `/v1/users/${urlId('customer', id)}`,
      // D-06: refresh-before-expiry cable token; returns { cable_token }.
      cableAuth: '/v1/users/cable_auth',
    },
    // Quick 260622-itv (PROF-02) — top-level address destroy route. `DELETE
    // /v1/addresses/:id` removes a single address and renders 200 `{}` (NOT the
    // full customer). This SUPERSEDES the 06.2 D-10 "no destroy route /
    // PATCH-resend" strategy, which was a silent no-op: Rails
    // `accepts_nested_attributes_for` never deletes OMITTED nested rows.
    // `[VERIFIED: ../api/config/routes.rb:182 `resources :addresses` →
    // v1/addresses_controller#destroy (app/controllers/v1/addresses_controller.rb:38-43
    // `render json: {}, status: :ok`); exercised by
    // ../api/spec/requests/v1/addresses_requests_spec.rb:12-17
    // `delete v1_address_path(address.id)` → 200]`
    addresses: {
      delete: (addressId: number) => `/v1/addresses/${addressId}`,
    },
    tenants: {
      config: (subdomain: string) => `/v1/tenants/${subdomain}`,
      customizations: (subdomain: string) =>
        `/v1/tenants/${subdomain}/customizations`,
    },
    appointments: {
      share: (token: string) => `/v1/appointments/share/${token}`,
      create: '/v1/appointments',
      get: (id: RecordId) => `/v1/appointments/${urlId('appointment', id)}`,
      shareGenerate: (id: RecordId) =>
        `/v1/appointments/${urlId('appointment', id)}/share`,
    },
    availability: {
      availableDates: '/v1/availability/available_dates',
      availableTimeSlots: '/v1/availability/available_time_slots',
    },
    serviceCategories: '/v1/service_categories',
    tokens: {
      auth: (token: string) => `/v1/tokens/${token}/auth`,
    },
    comments: {
      byDate: '/v1/comments/by_date',
    },
  },
  v2: {
    customers: {
      resetPassword: (customerId: string) =>
        `/v2/customers/${customerId}/reset_password`,
      addresses: (id: RecordId) =>
        `/v2/customers/${urlId('customer', id)}/addresses`,
      address: (id: RecordId, addressId: number) =>
        `/v2/customers/${urlId('customer', id)}/addresses/${addressId}`,
      invoices: (id: RecordId) =>
        `/v2/customers/${urlId('customer', id)}/invoices`,
      // Phase 5.5 (PODZ-1661) — customer-scoped quote-detail routes. The
      // PROCEED URL itself is HATEOAS-issued via `quote.links.quote_proceed.href`
      // and is POSTed verbatim by the service — `quoteProceed` is registered
      // here only for tooling/cross-reference (do NOT use it to reconstruct
      // URLs in callers; port research §6 Blocker B). See QuoteService.
      quotes: (customerId: RecordId, quoteId: RecordId) =>
        `/v2/customers/${urlId('customer', customerId)}/quotes/${urlId('quote', quoteId)}`,
      quoteProceed: (customerId: RecordId, quoteId: RecordId) =>
        `/v2/customers/${urlId('customer', customerId)}/quotes/${urlId('quote', quoteId)}/proceed`,
      quoteFinancingOptions: (customerId: RecordId, quoteId: RecordId) =>
        `/v2/customers/${urlId('customer', customerId)}/quotes/${urlId('quote', quoteId)}/financing_options`,
      // TC-708: the API sends no sign link, so the path is built here like its siblings.
      quoteSign: (customerId: RecordId, quoteId: RecordId) =>
        `/v2/customers/${urlId('customer', customerId)}/quotes/${urlId('quote', quoteId)}/sign`,
    },
    // Phase 5.2 (BILL-02) — invoice detail. NOT customer-scoped: the server
    // owns `authorize! :read, @invoice` in v2/invoices_controller#show, so the
    // path takes only the invoice id (contrast the customer-scoped quote route).
    invoices: {
      get: (id: RecordId) => `/v2/invoices/${urlId('invoice', id)}`,
      // TC-718: the customer's work-completed signature (api invoices#sign).
      sign: (id: RecordId) => `/v2/invoices/${urlId('invoice', id)}/sign`,
    },
    // Phase 5.3 (BILL-04/05/06, D-11) — pay-modal charge execution. Route is
    // nested under invoices with the `:invoice_id` path param
    // `[VERIFIED: ../api/app/controllers/v2/transactions_controller.rb]`.
    // The `next_payment_key` idempotency token is sent as a URL QUERY PARAM
    // (also body-permitted) — there is NO `Idempotency-Key` HTTP header
    // `[VERIFIED: transactions_controller.rb:269]`. The gateway is resolved
    // server-side from tenant config; the client never sends a gatewayType.
    transactions: {
      create: (invoiceId: RecordId) =>
        `/v2/invoices/${urlId('invoice', invoiceId)}/transactions`,
    },
    // Phase 8 (PAYLINK-02, D-13) — pay-by-link two-token exchange. The opaque
    // link token from the public `/pay/:token` URL is POSTed here to exchange it
    // for a scoped temp_token + invoice id. The token is wrapped in
    // `encodeURIComponent` (ASVS V5 input validation) so it stays an opaque URL
    // segment and cannot introduce path traversal via a crafted `:token`. This
    // is the single centralized path builder `InvoiceService.exchangePayToken`
    // calls; the charge/transactions path is Phase 9 — do NOT add it here.
    pay: {
      exchange: (token: string) =>
        `/v2/pay/${encodeURIComponent(token)}/exchange`,
    },
    // No-login job quotes link: exchanges the link token for a session scoped to
    // that job's quotes. Encoded for the same reason as `pay.exchange`.
    links: {
      exchange: (token: string) =>
        `/v2/links/${encodeURIComponent(token)}/exchange`,
    },
    appointments: {
      update: (id: RecordId) => `/v2/appointments/${urlId('appointment', id)}`,
      cancel: (id: RecordId) =>
        `/v2/appointments/${urlId('appointment', id)}/cancel`,
    },
    jobs: {
      get: (id: RecordId) => `/v2/jobs/${urlId('job', id)}`,
      documentsReserve: (id: RecordId) =>
        `/v2/jobs/${urlId('job', id)}/documents/reserve`,
      documentsComplete: (id: RecordId, key: string) =>
        `/v2/jobs/${urlId('job', id)}/documents/complete?s3_key=${key}`,
      videosInitiateMultipart: (id: RecordId) =>
        `/v2/jobs/${urlId('job', id)}/videos/initiate_multipart`,
      videosCompleteMultipart: (id: RecordId) =>
        `/v2/jobs/${urlId('job', id)}/videos/complete_multipart`,
    },
    videos: {
      partUrls: '/v2/videos/part_urls',
      abortUpload: '/v2/videos/abort_upload',
    },
  },
  reports: {
    consumerAppointments: '/reports/consumer/appointments.json',
    consumerInvoices: '/reports/consumer/invoices.json',
    consumerQuotes: '/reports/consumer/quotes.json',
  },
  // Phase 5.3 (BILL-10, D-11) — authoritative payment-method list + per-key SDK
  // creds (web_id / tokenization_key / environment / card_savable). Server
  // filters to the customer-allowed set and returns a plain JSON array
  // `[VERIFIED: ../api/app/models/payment_methods.rb:10-117]`.
  //
  // WIRE PATH = `/v2/payment_methods` `[VERIFIED 2026-06-05]`. The route line
  // `get '/payment_methods', to: 'payment_methods#global_index'`
  // `[../api/config/routes/v2_api.rb:10]` is drawn inside the
  // `versioned_api(2)` block (`../api/config/routes.rb:11` → `draw :v2_api`),
  // and `versioned_api` prepends `path: { value: "v2" }`
  // `[../api/config/routes/versioned_api.rb:7-10]`. The leading slash in the
  // route DSL is relative to that version scope, so the mounted wire path is
  // `/v2/payment_methods`, NOT a top-level `/payment_methods`. The controller
  // is `V2::PaymentMethodsController#global_index`
  // `[../api/app/controllers/v2/payment_methods_controller.rb:3,5,47]`, same
  // V2 module + mount as the VERIFIED `/v2/invoices/:id/transactions` route.
  // (The earlier `reports.*`-style top-level guess misread the version scope.)
  paymentMethods: '/v2/payment_methods',
} as const;
